import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMindmapRequest,
  CreateLinkRequest,
  CreateOrgRequest,
  CreateTeamRequest,
  InvitePreview,
  MapLink,
  PublicMapResponse,
  InviteRequest,
  InviteResponse,
  MeResponse,
  MapShare,
  Member,
  OrgRole,
  MindmapDetail,
  MindmapSummary,
  NodeAttachment,
  ShareMapRequest,
  Organization,
  SpaceRef,
  SpacesResponse,
  Team,
  UpdateMindmapRequest,
  UpdateOrgRequest,
} from "@etus-mindmap/shared/api";
import { apiDownload, apiFetch, apiUpload } from "./client";
import { spaceHeader, useActiveSpace } from "./space-context";

// The only module that touches getAccessTokenSilently + apiFetch.
// Components consume these hooks — never `fetch` directly. O header X-Space
// (espaço ativo) é injetado aqui em toda chamada.

// Erros do Auth0 em que a renovação silenciosa não dá — precisa de interação do
// usuário (a sessão/consentimento não pode ser obtida sem tela).
const INTERACTIVE_AUTH_ERRORS = new Set([
  "consent_required",
  "login_required",
  "interaction_required",
  "missing_refresh_token",
]);

// Busca o access token; se o Auth0 exigir interação (ex.: consent_required,
// que o Chrome dispara ao bloquear cookies de terceiros na renovação via
// iframe), redireciona para o login — onde O USUÁRIO consente — em vez de
// estourar "Consent required" na tela. Preserva o destino atual.
function useToken() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  return async function getToken(): Promise<string> {
    try {
      return await getAccessTokenSilently();
    } catch (e) {
      const code = (e as { error?: string })?.error;
      if (code && INTERACTIVE_AUTH_ERRORS.has(code)) {
        await loginWithRedirect({
          appState: { returnTo: window.location.pathname + window.location.search },
        });
        // loginWithRedirect navega para fora; esta promise nunca resolve de fato.
        return new Promise<string>(() => {});
      }
      throw e;
    }
  };
}

function useAuthedFetch() {
  const getToken = useToken();
  const { activeSpace } = useActiveSpace();
  const space = spaceHeader(activeSpace);
  return async function authedFetch<T>(
    path: string,
    opts: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const token = await getToken();
    return apiFetch<T>(path, { ...opts, token, space });
  };
}

export function useMe() {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["me"],
    queryFn: () => authedFetch<MeResponse>("/api/auth/me"),
  });
}

export function useMaps() {
  const authedFetch = useAuthedFetch();
  const { activeSpace } = useActiveSpace();
  return useQuery({
    // chave inclui o espaço ativo → troca de espaço refaz a busca
    queryKey: ["maps", spaceHeader(activeSpace)],
    queryFn: () => authedFetch<MindmapSummary[]>("/api/maps"),
  });
}

// Lista mapas de um espaço ESPECÍFICO (não o ativo). Usado para vincular um nó
// a outro mapa da MESMA org/espaço do mapa aberto — o espaço ativo global pode
// ser outro (abrir um mapa não muda o espaço ativo). Mesma chave de cache que
// useMaps para reaproveitar dados já buscados.
export function useMapsInSpace(space: SpaceRef | null) {
  const getToken = useToken();
  return useQuery({
    queryKey: ["maps", space ? spaceHeader(space) : "none"],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<MindmapSummary[]>("/api/maps", { token, space: spaceHeader(space as SpaceRef) });
    },
    enabled: space != null,
  });
}

// Mapas são endereçados por uuid (string), não pelo id sequencial.
export function useMap(uuid: string | null) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["map", uuid],
    queryFn: () => authedFetch<MindmapDetail>(`/api/maps/${uuid}`),
    enabled: uuid != null,
  });
}

export function useCreateMap() {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateMindmapRequest) =>
      authedFetch<MindmapDetail>("/api/maps", { method: "POST", body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maps"] }),
  });
}

export function useUpdateMap(uuid: string) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateMindmapRequest) =>
      authedFetch<MindmapDetail>(`/api/maps/${uuid}`, { method: "PUT", body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maps"] }),
  });
}

export function useDeleteMap() {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => authedFetch<{ ok: true }>(`/api/maps/${uuid}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["maps"] }),
  });
}

// ---- Anexos de nós ---------------------------------------------------------

export function useUploadFile(mapUuid: string) {
  const getToken = useToken();
  return useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken();
      return apiUpload<NodeAttachment>(`/api/maps/${mapUuid}/files`, file, token);
    },
  });
}

export function useDeleteFile(mapUuid: string) {
  const authedFetch = useAuthedFetch();
  return useMutation({
    mutationFn: (fileId: string) =>
      authedFetch<{ ok: true }>(`/api/maps/${mapUuid}/files/${fileId}`, { method: "DELETE" }),
  });
}

// Baixa o anexo autenticado e dispara o save do browser com o nome original.
export function useFileDownloader(mapUuid: string) {
  const getToken = useToken();
  return async (att: NodeAttachment) => {
    const token = await getToken();
    const blob = await apiDownload(`/api/maps/${mapUuid}/files/${att.id}`, token);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.name;
    a.click();
    URL.revokeObjectURL(url);
  };
}

// ---- Colaboração: espaços, orgs, times, membros, convites ------------------

type Scope = "org" | "team";

export function useSpaces() {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["spaces"],
    queryFn: () => authedFetch<SpacesResponse>("/api/spaces"),
  });
}

export function useCreateOrg() {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateOrgRequest) =>
      authedFetch<Organization>("/api/orgs", { method: "POST", body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spaces"] }),
  });
}

export function useCreateTeam() {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateTeamRequest) =>
      authedFetch<Team>("/api/teams", { method: "POST", body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spaces"] }),
  });
}

export function useOrg(id: number | null) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["org", id],
    queryFn: () => authedFetch<Organization>(`/api/orgs/${id}`),
    enabled: id != null,
  });
}

export function useUpdateOrg(id: number) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateOrgRequest) =>
      authedFetch<Organization>(`/api/orgs/${id}`, { method: "PATCH", body: req }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org", id] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
    },
  });
}

export function useUploadOrgLogo(id: number) {
  const getToken = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const token = await getToken();
      return apiUpload<{ ok: true }>(`/api/orgs/${id}/logo`, file, token);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org", id] });
      qc.invalidateQueries({ queryKey: ["spaces"] });
    },
  });
}

// Busca o logo autenticado como blob e devolve um object URL (para <img>).
export function useOrgLogo(id: number | null, enabled: boolean) {
  const getToken = useToken();
  return useQuery({
    queryKey: ["org-logo", id],
    queryFn: async () => {
      const token = await getToken();
      const blob = await apiDownload(`/api/orgs/${id}/logo`, token);
      return URL.createObjectURL(blob);
    },
    enabled: id != null && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useMembers(scope: Scope, id: number | null) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["members", scope, id],
    queryFn: () => authedFetch<Member[]>(`/api/${scope}s/${id}/members`),
    enabled: id != null,
  });
}

export function useInviteMember(scope: Scope, id: number) {
  const authedFetch = useAuthedFetch();
  return useMutation({
    mutationFn: (req: InviteRequest) =>
      authedFetch<InviteResponse>(`/api/${scope}s/${id}/invites`, { method: "POST", body: req }),
  });
}

export function useRemoveMember(scope: Scope, id: number) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      authedFetch<{ ok: true }>(`/api/${scope}s/${id}/members/${encodeURIComponent(email)}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", scope, id] }),
  });
}

// Trocar o papel de um membro (member↔admin). PATCH já existe no worker
// (setMemberRole); só o front não chamava.
export function useUpdateMemberRole(scope: Scope, id: number) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: OrgRole }) =>
      authedFetch<{ ok: true }>(`/api/${scope}s/${id}/members/${encodeURIComponent(email)}`, {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", scope, id] }),
  });
}

export function useInvitePreview(token: string) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["invite", token],
    queryFn: () => authedFetch<InvitePreview>(`/api/invites/${token}`),
  });
}

// ---- Links públicos (anônimo, sem auth) ------------------------------------

export function usePublicMap(token: string) {
  return useQuery({
    queryKey: ["public-map", token],
    queryFn: () => apiFetch<PublicMapResponse>(`/api/shared/${token}`),
    retry: false,
  });
}

export function usePublicSave(token: string) {
  return useMutation({
    mutationFn: (req: UpdateMindmapRequest) =>
      apiFetch<PublicMapResponse>(`/api/shared/${token}`, { method: "PUT", body: req }),
  });
}

export function useLinks(uuid: string) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["links", uuid],
    queryFn: () => authedFetch<MapLink[]>(`/api/maps/${uuid}/links`),
  });
}

export function useCreateLink(uuid: string) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateLinkRequest) =>
      authedFetch<MapLink>(`/api/maps/${uuid}/links`, { method: "POST", body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["links", uuid] }),
  });
}

export function useRevokeLink(uuid: string) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      authedFetch<{ ok: true }>(`/api/maps/${uuid}/links/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["links", uuid] }),
  });
}

// ---- Compartilhamento de mapa por usuário ----------------------------------

export function useShares(uuid: string) {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ["shares", uuid],
    queryFn: () => authedFetch<MapShare[]>(`/api/maps/${uuid}/shares`),
  });
}

export function useAddShare(uuid: string) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ShareMapRequest) =>
      authedFetch<{ ok: true }>(`/api/maps/${uuid}/shares`, { method: "POST", body: req }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shares", uuid] }),
  });
}

export function useRemoveShare(uuid: string) {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      authedFetch<{ ok: true }>(`/api/maps/${uuid}/shares/${encodeURIComponent(email)}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shares", uuid] }),
  });
}

export function useAcceptInvite() {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      authedFetch<{ scope: Scope; scopeId: number }>(`/api/invites/${token}/accept`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spaces"] }),
  });
}
